import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { get, post } from '../../utils/api';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { cardFeeRadioSuffix, computeClientFeeTotal, mergedFeeDisplay } from '../../utils/donationFeeConfig';

const DEFAULT_PLEDGE_THUMBNAIL = '/assets/images/ICCT-Logo-trans.webp';

function formatMoney(n) {
  const x = Number(n || 0);
  return `$${x.toFixed(2)}`;
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function PledgeSummaryCard({ pledge }) {
  const items = Array.isArray(pledge?.line_items) ? pledge.line_items : [];
  const firstItem = items[0] || null;
  const rawName = String(firstItem?.name || 'Pledge').trim() || 'Pledge';
  const productName = rawName.includes(' - ') ? rawName.split(' - ')[0].trim() : rawName;
  const variationName = (() => {
    const named =
      String(firstItem?.variation_name || firstItem?.variation_label || firstItem?.attribute_summary || '').trim();
    if (named) return named;
    // Fallback: if line item name is "Product - Variation", show variation part.
    if (rawName.includes(' - ')) {
      const parts = rawName.split(' - ').map((p) => p.trim()).filter(Boolean);
      if (parts.length > 1) return parts.slice(1).join(' - ');
    }
    return '';
  })();
  const imageUrl = String(firstItem?.image_url || '').trim() || DEFAULT_PLEDGE_THUMBNAIL;
  const d = pledge?.date_created_gmt || pledge?.date_created;
  const dateStr = d ? new Date(d).toLocaleDateString() : '—';

  const summaryBoxes = [
    { label: 'Date pledged', value: dateStr },
    { label: 'Pledge amount', value: formatMoney(pledge?.pledge_total) },
    { label: 'Paid', value: formatMoney(pledge?.paid_total) },
    { label: 'Balance', value: formatMoney(pledge?.balance_total), emphasize: true },
  ];

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Stack direction="row" spacing={1.5} alignItems="stretch" sx={{ minWidth: 0 }}>
            <Box
              component="img"
              src={imageUrl}
              alt={productName}
              onError={(e) => {
                e.currentTarget.src = DEFAULT_PLEDGE_THUMBNAIL;
              }}
              sx={{
                width: 92,
                height: '100%',
                minHeight: 96,
                borderRadius: 1.5,
                objectFit: 'cover',
                border: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {productName}
              </Typography>
              {variationName ? (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25, fontSize: '1.05rem' }}>
                  {variationName}
                </Typography>
              ) : null}
            </Box>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent={{ sm: 'flex-end' }}
            alignSelf={{ xs: 'stretch', md: 'flex-start' }}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            {summaryBoxes.map((entry) => (
              <Box
                key={entry.label}
                sx={{
                  minWidth: 132,
                  px: 1.25,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'grey.50',
                  textAlign: 'right',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {entry.label}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, color: entry.emphasize ? 'warning.dark' : 'text.primary', fontSize: '1.15rem', whiteSpace: 'nowrap' }}
                >
                  {entry.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function PaymentStep({
  devoteeId,
  pledgeOrderId,
  customerComment,
  setCustomerComment,
  onSuccess,
  onError,
  onPaymentElementChange,
  asyncQueue,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = React.useState(false);
  const [processingOpen, setProcessingOpen] = React.useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setProcessingOpen(true);
    onError('');
    try {
      const returnUrl = `${window.location.origin}/devotees/${devoteeId}/pledges/${pledgeOrderId}/payment-receipt`;
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: returnUrl,
        },
      });
      if (error) {
        onError(error.message || 'Payment failed');
        setProcessingOpen(false);
        setBusy(false);
        return;
      }
      // Card/wallets confirm as 'succeeded'; ACH confirms as 'processing' and is queued
      // immediately (the payments-app flips it on settlement) — treat that as success too.
      const ok =
        paymentIntent &&
        (paymentIntent.status === 'succeeded' || (asyncQueue && paymentIntent.status === 'processing'));
      if (ok) {
        const fin = await post(
          `/portal/pledges/${encodeURIComponent(String(pledgeOrderId))}/finalize-payment`,
          {
            paymentIntentId: paymentIntent.id,
            customerComment,
          },
          { timeout: 120000 }
        );
        onSuccess(fin);
      } else {
        onError('Payment was not completed. If you were redirected, open the receipt link from your browser.');
        setProcessingOpen(false);
        setBusy(false);
      }
    } catch (e) {
      onError(e?.message || 'Payment failed');
      setProcessingOpen(false);
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={processingOpen} disableEscapeKeyDown onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Payment processing</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="body1">Your payment is in progress. Please wait and do not close this page.</Typography>
          </Stack>
        </DialogContent>
      </Dialog>
      <Stack spacing={2} sx={{ pointerEvents: processingOpen ? 'none' : 'auto', opacity: processingOpen ? 0.6 : 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Payment
        </Typography>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
          <PaymentElement
            options={{
              wallets: {
                link: 'never',
              },
            }}
            onChange={(event) => {
              if (!onPaymentElementChange) return;
              // Do not gate on `event.empty`: after switching tabs (e.g. to US bank), the form
              // is still incomplete so `empty` is true, but `value.type` already reflects the selection.
              const v = event.value;
              const typ = v && typeof v.type === 'string' ? v.type : null;
              onPaymentElementChange(typ);
            }}
          />
        </Box>
        <TextField
          label="Comments (optional)"
          multiline
          minRows={2}
          fullWidth
          value={customerComment}
          onChange={(e) => setCustomerComment(e.target.value)}
          placeholder="Add a note for the temple office…"
          disabled={processingOpen}
        />
        <Button variant="contained" size="large" disabled={!stripe || busy || processingOpen} onClick={handlePay}>
          {busy ? <CircularProgress size={22} color="inherit" /> : 'Pay now'}
        </Button>
      </Stack>
    </>
  );
}

export default function PortalPledgePayPage() {
  const { id: devoteeId, pledgeOrderId } = useParams();
  const navigate = useNavigate();
  const { staff } = useAuth();

  const [loadErr, setLoadErr] = React.useState('');
  const [pledge, setPledge] = React.useState(null);
  const [step, setStep] = React.useState(1);
  const [payMode, setPayMode] = React.useState('full');
  const [partialInput, setPartialInput] = React.useState('');
  const [fundingType, setFundingType] = React.useState('card_wallet');
  const [intentErr, setIntentErr] = React.useState('');
  const [clientSecret, setClientSecret] = React.useState('');
  const [publishableKey, setPublishableKey] = React.useState('');
  const [stripePromise, setStripePromise] = React.useState(null);
  const [amounts, setAmounts] = React.useState(null);
  const [paymentIntentId, setPaymentIntentId] = React.useState('');
  const [asyncQueue, setAsyncQueue] = React.useState(false);
  const [customerComment, setCustomerComment] = React.useState('');
  const [serverFeeUi, setServerFeeUi] = React.useState(null);
  const [pledgeIntentFeeUi, setPledgeIntentFeeUi] = React.useState(null);
  const feeSyncTimerRef = React.useRef(null);
  const stepRef = React.useRef(step);
  const paymentIntentIdRef = React.useRef(paymentIntentId);
  stepRef.current = step;
  paymentIntentIdRef.current = paymentIntentId;

  React.useEffect(() => {
    if (!staff?.subject || !devoteeId || !pledgeOrderId) return;
    if (String(staff.subject) !== String(devoteeId)) return;
    let cancelled = false;
    (async () => {
      setLoadErr('');
      try {
        const res = await get(`/portal/pledges/${encodeURIComponent(String(pledgeOrderId))}/pay-summary`, {});
        if (cancelled) return;
        const p = res?.pledge;
        if (!p) {
          setLoadErr('Pledge not found.');
          return;
        }
        const bal = Number(p.balance_total ?? 0);
        if (bal <= 0) {
          setLoadErr('This pledge has no remaining balance.');
          return;
        }
        setPledge(p);
      } catch (e) {
        if (!cancelled) setLoadErr(e?.message || 'Failed to load pledge');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [staff?.subject, devoteeId, pledgeOrderId]);

  React.useEffect(() => {
    setPledgeIntentFeeUi(null);
  }, [pledgeOrderId]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await get('/public/donate/fee-ui', {});
        if (!cancelled && r?.feeUi) setServerFeeUi(r.feeUi);
      } catch {
        /* REACT_APP_* fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const balance = pledge ? round2(Number(pledge.balance_total ?? 0)) : 0;
  const pledgeHeaderImage = React.useMemo(() => {
    const items = Array.isArray(pledge?.line_items) ? pledge.line_items : [];
    const u = String(items[0]?.image_url || '').trim();
    return u || undefined;
  }, [pledge]);
  const pledgeHeaderAlt = React.useMemo(() => {
    const items = Array.isArray(pledge?.line_items) ? pledge.line_items : [];
    const n = String(items[0]?.name || '').trim();
    return n || 'Pledge';
  }, [pledge]);

  const baseAmount = React.useMemo(() => {
    if (!pledge) return 0;
    if (payMode === 'full') return balance;
    const raw = String(partialInput || '').replace(/[^0-9.]/g, '');
    const p = round2(Number.parseFloat(raw) || 0);
    return p;
  }, [pledge, payMode, partialInput, balance]);

  const feeDisplay = React.useMemo(
    () => mergedFeeDisplay(serverFeeUi, pledgeIntentFeeUi),
    [serverFeeUi, pledgeIntentFeeUi]
  );
  const preview = React.useMemo(
    () => computeClientFeeTotal(baseAmount, fundingType, feeDisplay),
    [baseAmount, fundingType, feeDisplay]
  );
  const displayPreview =
    step === 4 && amounts
      ? { base: amounts.base, fee: amounts.fee, total: amounts.total }
      : preview;

  React.useEffect(
    () => () => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
    },
    []
  );

  const queueFeeSyncForElement = React.useCallback(
    (paymentMethodType) => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
      feeSyncTimerRef.current = setTimeout(async () => {
        const pi = paymentIntentIdRef.current;
        if (!pi || stepRef.current !== 4) return;
        try {
          setIntentErr('');
          const res = await post(
            `/portal/pledges/${encodeURIComponent(String(pledgeOrderId))}/payment-intent`,
            {
              mode: 'sync-fee',
              syncFee: true,
              paymentIntentId: pi,
              paymentMethodType,
            },
            { timeout: 30000 }
          );
          if (res?.amounts) setAmounts(res.amounts);
        } catch (e) {
          setIntentErr(e?.message || 'Could not update fees for the selected payment method');
        }
      }, 400);
    },
    [pledgeOrderId]
  );

  const showAmountCard = step === 1 || step === 2 || step === 4;
  const showMethodCard = step === 2 || step === 4;
  const amountLocked = step === 4;
  const methodLocked = step === 4;

  const handleCreateIntent = async () => {
    setIntentErr('');
    if (!pledge) return;
    if (payMode === 'partial') {
      const raw = String(partialInput || '').replace(/[^0-9.]/g, '');
      const p = round2(Number.parseFloat(raw) || 0);
      if (p < 1) {
        setIntentErr('Enter a partial amount of at least $1.00.');
        return;
      }
      if (p > balance + 0.001) {
        setIntentErr('Partial amount cannot exceed the balance.');
        return;
      }
    }
    try {
      const res = await post(
        `/portal/pledges/${encodeURIComponent(String(pledgeOrderId))}/payment-intent`,
        {
          mode: 'create',
          payMode,
          partialAmount:
            payMode === 'partial'
              ? round2(Number.parseFloat(String(partialInput || '').replace(/[^0-9.]/g, '')) || 0)
              : undefined,
          fundingType,
        },
        { timeout: 60000 }
      );
      const cs = res?.clientSecret;
      const pk = res?.publishableKey;
      if (!cs || !pk) {
        setIntentErr('Could not start payment. Check Stripe and order-commit configuration.');
        return;
      }
      setClientSecret(cs);
      setPublishableKey(pk);
      setAmounts(res.amounts || null);
      setAsyncQueue(!!res.asyncQueue);
      setPledgeIntentFeeUi(res?.feeUi || null);
      setPaymentIntentId(String(res?.paymentIntentId || ''));
      setStripePromise(loadStripe(pk));
      setStep(4);
    } catch (e) {
      setIntentErr(e?.message || 'Failed to create payment');
    }
  };

  const onPaySuccess = (fin) => {
    navigate(`/devotees/${devoteeId}/pledges/${pledgeOrderId}/payment-receipt`, {
      replace: true,
      state: { ...(fin || {}) },
    });
  };

  if (loadErr) {
    return (
      <>
        <PortalPageHeader title="Pay pledge" />
        <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert>
        <Button variant="outlined" onClick={() => navigate(`/devotees/${devoteeId}/pledges`)}>
          Back to Pledges
        </Button>
      </>
    );
  }

  if (!pledge) {
    return (
      <>
        <PortalPageHeader title="Pay pledge" />
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      </>
    );
  }

  return (
    <>
      <PortalPageHeader title="Pay pledge balance" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {feeDisplay.feeCaption ||
          `Pay down your pledge securely. Card and digital wallets: ${feeDisplay.pct ?? 2.5}% processing fee. ACH has no fee.`}
      </Typography>

      <PledgeSummaryCard pledge={pledge} />

      {showAmountCard && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              1. Contribution amount
            </Typography>
            <FormControl component="fieldset" disabled={amountLocked}>
              <RadioGroup
                value={payMode}
                onChange={(e) => setPayMode(e.target.value)}
                sx={{ alignItems: 'flex-start', gap: 0.75 }}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio />}
                  disabled={amountLocked}
                  label={
                    <Typography component="span" variant="body1" sx={{ fontSize: '1.05rem', fontWeight: 500 }}>
                      {`Pay in full (${formatMoney(balance)})`}
                    </Typography>
                  }
                  sx={{ alignItems: 'center', ml: 0, mr: 0, mb: 0 }}
                />
                <FormControlLabel
                  value="partial"
                  control={<Radio />}
                  disabled={amountLocked}
                  sx={{ alignItems: 'center', ml: 0, mr: 0, mb: 0, '.MuiFormControlLabel-label': { display: 'block' } }}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1.25} component="span" sx={{ py: 0.25, pl: 0 }}>
                      <Typography component="span" variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: '1.05rem', fontWeight: 500 }}>
                        Pay a partial amount
                      </Typography>
                      <TextField
                        size="small"
                        label="Contribution amount (USD)"
                        value={partialInput}
                        onChange={(e) => setPartialInput(e.target.value)}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          if (!amountLocked) setPayMode('partial');
                        }}
                        onFocus={() => {
                          if (!amountLocked) setPayMode('partial');
                        }}
                        placeholder="0.00"
                        disabled={amountLocked || payMode !== 'partial'}
                        inputProps={{ inputMode: 'decimal' }}
                        sx={{
                          width: { xs: '100%', sm: 160 },
                          minWidth: 120,
                          '& .MuiInputBase-root': { height: 40 },
                        }}
                      />
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
            {step === 1 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (payMode === 'partial') {
                      const raw = String(partialInput || '').replace(/[^0-9.]/g, '');
                      const p = round2(Number.parseFloat(raw) || 0);
                      if (p < 1 || p > balance + 0.001) return;
                    }
                    setStep(2);
                  }}
                  disabled={
                    payMode === 'partial' &&
                    (!partialInput || Number.parseFloat(String(partialInput).replace(/[^0-9.]/g, '')) <= 0)
                  }
                >
                  Continue
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {showMethodCard && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              2. Payment method options
            </Typography>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
            >
              <FormControl component="fieldset" disabled={methodLocked} sx={{ flex: '1 1 auto' }}>
                <RadioGroup value={fundingType} onChange={(e) => setFundingType(e.target.value)}>
                  <FormControlLabel
                    value="card_wallet"
                    control={<Radio />}
                    disabled={methodLocked}
                    label={`Credit / debit card, Apple Pay, Google Pay (${cardFeeRadioSuffix(feeDisplay)})`}
                  />
                  <FormControlLabel
                    value="ach"
                    control={<Radio />}
                    disabled={methodLocked}
                    label="ACH (US bank account) — no processing fee"
                  />
                </RadioGroup>
              </FormControl>
              <Box
                sx={{
                  width: { xs: '100%', md: 340 },
                  px: 1.5,
                  py: 1.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'grey.50',
                }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', textAlign: 'right' }}>
                    Subtotal (pledge payment) <strong>{formatMoney(displayPreview.base)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', textAlign: 'right' }}>
                    {feeDisplay.feeName} <strong>{formatMoney(displayPreview.fee)}</strong>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, mt: 0.25, fontSize: '1.08rem', textAlign: 'right' }}
                  >
                    Total charge: {formatMoney(displayPreview.total)}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
            {intentErr && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {intentErr}
              </Alert>
            )}
            {step === 2 && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button variant="contained" onClick={handleCreateIntent}>
                  Continue to payment
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {step === 4 && stripePromise && clientSecret && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            {amounts && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Charging <strong>{formatMoney(amounts.total)}</strong>
                {amounts.fee > 0 ? ` (includes ${formatMoney(amounts.fee)} fee)` : ''}.
              </Typography>
            )}
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentStep
                devoteeId={devoteeId}
                pledgeOrderId={pledgeOrderId}
                customerComment={customerComment}
                setCustomerComment={setCustomerComment}
                onSuccess={onPaySuccess}
                onError={setIntentErr}
                onPaymentElementChange={queueFeeSyncForElement}
                asyncQueue={asyncQueue}
              />
            </Elements>
            {intentErr && step === 4 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {intentErr}
              </Alert>
            )}
            <Button sx={{ mt: 2 }} variant="text" onClick={() => navigate(`/devotees/${devoteeId}/pledges`)}>
              Cancel
            </Button>
          </CardContent>
        </Card>
      )}
    </>
  );
}
